import {useCallback} from 'react'
import {useSitePreferences} from '../hooks/use-site-preferences'

export const FEW_ITEMS_LEFT_QUANTITY_PREF_IDS = [
    'fewItemsLeftQuantityEnabled',
    'fewItemsLeftQuantityThreshold'
]

/**
 * Generates a fomo message based on the product's inventory status and site preferences.
 *
 * @function getFomoMessage
 * @param {Object} product
 * @param {{ enabled: boolean, threshold: number }} [{enabled, threshold}={}]
 * @returns {{ messageKey: string, size?: string, count?: number } | null}
 */
export function getFomoMessage(product, {enabled, threshold} = {}) {
    const inventory = product?.inventory
    const orderable = inventory?.orderable ?? true
    const level = inventory?.stockLevel ?? inventory?.ats
    if (orderable) {
        if (product?.c_fewItemsLeft) {
            if (enabled && level > 0 && level <= threshold) {
                return {messageKey: 'few_left_with_count', count: level}
            }
            return {messageKey: 'few_left'}
        }

        if (product?.c_isAlmostGone) {
            return {
                messageKey: 'almost_gone_with_size',
                size: product?.variationValues?.size
            }
        }
    }

    return null
}

/**
 * @function useFomoMessaging A hook that returns a function to generate a fomo message for a given product based on site preferences.
 *
 * @param {boolean} showFewItemsLeft
 * @return {{ messageKey: string, size?: string, count?: number } | null}
 */
export function useFomoMessaging(showFewItemsLeft) {
    const {data: prefs} = useSitePreferences(FEW_ITEMS_LEFT_QUANTITY_PREF_IDS, {
        enabled: showFewItemsLeft
    })
    const enabled = prefs?.fewItemsLeftQuantityEnabled
    const threshold = prefs?.fewItemsLeftQuantityThreshold

    return useCallback(
        (product) => {
            return getFomoMessage(product, {enabled, threshold})
        },
        [showFewItemsLeft, enabled, threshold]
    )
}
